import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    enableProdMode,
    inject,
    isDevMode,
    provideAppInitializer,
    provideCheckNoChangesConfig,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import {
    provideRouter,
    withDebugTracing,
    withInMemoryScrolling,
} from '@angular/router';
import {
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalBroadcastService,
    MsalGuard,
    MsalInterceptor,
    MsalService,
} from '@azure/msal-angular';
import { provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import {
    ConstantsService,
    DARK_MODE_CLASS,
    FORBIDDEN_ROUTE,
    HttpErrorInterceptor,
    LemunzoPrimeNGPreset,
    LocalizationService,
    MultiTranslateHttpLoader,
    NOT_FOUND_BACK_LABEL_KEY,
    NOT_FOUND_BACK_ROUTE,
    NOT_FOUND_ROUTE,
    PlatformProviders,
} from 'lemunzo-shared';
import { ConfirmationService, MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { firstValueFrom, take } from 'rxjs';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { environment } from './environments/environment';
import {
    MSALGuardConfigFactory,
    MSALInstanceFactory,
    MSALInterceptorConfigFactory,
} from './setup/msal-factories';
import { provideApi } from 'lemunzo-api';

if (environment.production) {
    enableProdMode();
}

// AoT requires an exported function for factories
export function httpLoaderFactory(http: HttpClient) {
    return new MultiTranslateHttpLoader(http, [
        './assets/i18n/client/',
        './assets/i18n/shared/',
    ]);
}

bootstrapApplication(AppComponent, {
    providers: [
        ...(isDevMode()
            ? [
                  provideCheckNoChangesConfig({
                      exhaustive: true,
                      interval: 3000,
                  }),
              ]
            : []),
        provideRouter(
            routes,
            withInMemoryScrolling({
                scrollPositionRestoration: 'top',
            }),
            ...(environment.production ? [] : [withDebugTracing()])
        ),
        provideHttpClient(withInterceptorsFromDi()),
        providePrimeNG({
            theme: {
                preset: LemunzoPrimeNGPreset,
                options: {
                    darkModeSelector: `.${DARK_MODE_CLASS}`,
                },
            },
        }),
        PlatformProviders,
        ConfirmationService,
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
            fallbackLang: ConstantsService.DEFAULT_LANG,
        }),
        provideAppInitializer(() => {
            const localizationService = inject(LocalizationService);
            localizationService.getDefaultLanguage();
            return localizationService.defaultLanguage$.pipe(take(1));
        }),
        provideAppInitializer(() => {
            const localizationService = inject(LocalizationService);
            localizationService.getDefaultCountry();
            return localizationService.defaultCountry$.pipe(take(1));
        }),
        provideAppInitializer(() => {
            const localizationService = inject(LocalizationService);
            localizationService.loadLocalizedCountries();
            return localizationService.localizedCountries$.pipe(take(2));
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        {
            provide: FORBIDDEN_ROUTE,
            useValue: () => '/client/forbidden',
        },
        {
            provide: NOT_FOUND_ROUTE,
            useValue: '/client/not-found',
        },
        {
            provide: NOT_FOUND_BACK_ROUTE,
            useValue: '/client/profiles',
        },
        {
            provide: NOT_FOUND_BACK_LABEL_KEY,
            useValue: 'cz.notFound.backToProfiles',
        },
        // Purges accounts cached under a previously configured authority (e.g. before
        // a tenant migration) before the router's first navigation. MsalGuard treats
        // "any cached account exists" as "signed in" without checking which authority
        // it belongs to, so a stale foreign account would otherwise let it skip the
        // interactive login redirect entirely.
        provideAppInitializer(() => {
            const msalService = inject(MsalService);
            return firstValueFrom(msalService.initialize()).then(() => {
                const instance = msalService.instance;
                const configuredAuthorityHost = new URL(
                    environment.authConfig.authority
                ).host;
                const staleAccounts = instance
                    .getAllAccounts()
                    .filter(
                        (account) =>
                            account.environment !== configuredAuthorityHost
                    );
                return Promise.all(
                    staleAccounts.map((account) =>
                        instance.clearCache({ account })
                    )
                );
            });
        }),
        MsalService,
        MsalGuard,
        MsalBroadcastService,
        MessageService,
        // TODO CSD-953 When generated services are used everywhere, remove 'api/' from apiUrl in all environments
        //      and use plain 'environment.apiUrl' instead of 'environment.apiUrl.replace('/api/', '')''
        provideApi(environment.apiUrl.replace('/api/', '')),
    ],
}).catch((err) => console.error('Intercept err:', err));
